import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function NumberWithCommas(x: number | string) {
  const number = typeof x === "string" ? parseFloat(x) : x;
  return number
    .toFixed(2)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const convertCurrency = (
  amount: number | string,
  currencyCode = "USD",
  locale = "en-US",
) => {
  if (!amount)
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
    }).format(0);

  if (!currencyCode) return `${currencyCode}${NumberWithCommas(amount)}`;

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
  }).format(typeof amount === "string" ? parseFloat(amount) : amount);
};

// Date formatting. Before these existed, five screens each did it differently:
// raw `new Date(x).toLocaleString()` (browser locale, so it varied per operator),
// a local Intl.DateTimeFormat in socialMediaPosts, and moment in
// RewardHistoryItem. en-US matches the rest of this file (see convertCurrency)
// and is what the one explicit formatter already used.
const DATE_LOCALE = "en-US";

/** "Jan 05, 2026, 14:30" — the default for table cells and detail rows. */
export function formatDateTime(value: string | number | Date): string {
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(DATE_LOCALE, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

/** "Jan 05, 2026" */
export function formatDate(value: string | number | Date): string {
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(DATE_LOCALE, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
}

/** "14:30" — 24h, no seconds. */
export function formatTime(value: string | number | Date): string {
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(DATE_LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

type SpecialWordsType = Record<string, string>;

const SPECIAL_WORDS: SpecialWordsType = {
  cpi: "CPI",
  kpi: "KPI",
  id: "ID",
  api: "API",
};

export function toReadable(snakeCaseString: string): string {
  return snakeCaseString
    .split("_")
    .map(
      (word) =>
        SPECIAL_WORDS[word] || word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
}

export function truncateString(input: string): string {
  const maxLength = 25;
  if (input.length <= maxLength) {
    return input;
  } else {
    return input.slice(0, maxLength - 3) + "...";
  }
}

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
  });
};

export function extractPackageName(url: string) {
  try {
    const parsedUrl = new URL(url);
    const packageName = parsedUrl.searchParams.get("id");

    return packageName;
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
}

export function extractIosAppId(url: string) {
  try {
    const parsedUrl = new URL(url);
    const pathSegments = parsedUrl.pathname.split("/");
    const idSegment = pathSegments.find((segment) => segment.startsWith("id"));
    if (idSegment) {
      const appId = idSegment.substring(2);
      return appId;
    }
    console.error("No iOS app ID found in the URL");
    return null;
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
}

export function extractAdjustTrackerId(url: string) {
  const parsedUrl = new URL(url);
  const pathname = parsedUrl.pathname;
  const segments = pathname.split("/");
  const adjustTrackerId = segments.length > 1 ? segments[1] : null;

  return adjustTrackerId;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
